// Smartlake 업로드 엔진 — 순수 로직(DOM 이벤트·React 없음). 폴더 펼치기, 해시, 배치 계획, 동시 실행.
//
// 경계값은 실측에서 왔다: 웹 → Cloudflare Pages Function → Cloud Run(HTTP/1) 은 요청 본문 32MiB 에서 413 을 낸다.
// 그래서 요청 하나는 REQUEST_BYTE_CAP 아래로 자르고, 그보다 큰 파일 하나는 이 경로로는 올릴 수 없다(oversize).
// 서버 한도(50 파일·50MB)는 그대로지만 이 경로에서 실효 한도는 아래 값이다.

export const REQUEST_BYTE_CAP = 30 * 1024 * 1024; // 32MiB 에서 multipart 오버헤드 여유
export const REQUEST_FILE_CAP = 50; // 서버 multer files 한도
export const FILE_BYTE_CAP = REQUEST_BYTE_CAP; // 이 크기까지는 multipart 묶음, 그 위는 직접 업로드(GCS 세션)
export const DIRECT_BYTE_CAP = 2 * 1024 * 1024 * 1024; // 직접 업로드 상한(서버 MAX_DIRECT_BYTES 와 동일)

export interface LakeFile {
  file: File;
  /** 드롭/선택 기준 상대 경로(폴더 포함). sourceRef 로 보낸다. */
  path: string;
  size: number;
}

/** 운영체제·오피스가 남기는 부산물 — 던져 넣어도 의미가 없어 기본으로 건너뛴다. */
export function isJunkPath(path: string): boolean {
  const name = path.split('/').pop() ?? path;
  if (name === '.DS_Store' || name === 'Thumbs.db' || name === 'desktop.ini') return true;
  if (name.startsWith('~$')) return true; // Office 잠금 파일
  if (name.startsWith('._')) return true; // macOS AppleDouble
  if (name.startsWith('.')) return true; // 숨김 파일
  return false;
}

export function normalizePath(p: string): string {
  return p.replace(/\\/g, '/').replace(/^\/+/, '').replace(/\/{2,}/g, '/');
}

/** <input type=file> 결과 → LakeFile[]. webkitRelativePath 가 있으면(폴더 선택) 그 경로를 쓴다. */
export function filesFromInput(list: FileList | File[]): LakeFile[] {
  const out: LakeFile[] = [];
  for (const file of Array.from(list)) {
    const rel = (file as File & { webkitRelativePath?: string }).webkitRelativePath;
    out.push({ file, path: normalizePath(rel && rel.length ? rel : file.name), size: file.size });
  }
  return out;
}

// ── 드롭된 DataTransfer 에서 폴더까지 재귀로 펼친다(FileSystem Entry API; Chrome/Edge/Safari/Firefox 지원). ──
function readAllEntries(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  // readEntries 는 100개씩 끊어 준다 — 빈 배열이 올 때까지 반복.
  return new Promise((resolve, reject) => {
    const acc: FileSystemEntry[] = [];
    const step = () =>
      reader.readEntries((batch) => {
        if (!batch.length) return resolve(acc);
        acc.push(...batch);
        step();
      }, reject);
    step();
  });
}

async function walkEntry(entry: FileSystemEntry, prefix: string, out: LakeFile[]): Promise<void> {
  if (entry.isFile) {
    const file = await new Promise<File>((ok, err) => (entry as FileSystemFileEntry).file(ok, err));
    out.push({ file, path: normalizePath(prefix + entry.name), size: file.size });
    return;
  }
  if (entry.isDirectory) {
    const children = await readAllEntries((entry as FileSystemDirectoryEntry).createReader());
    for (const c of children) await walkEntry(c, prefix + entry.name + '/', out);
  }
}

/** 드롭 이벤트의 DataTransfer → 파일 목록(폴더 재귀). Entry API 가 없으면 files 로 폴백. */
export async function collectDropped(dt: DataTransfer): Promise<LakeFile[]> {
  const out: LakeFile[] = [];
  const items = Array.from(dt.items ?? []);
  const entries: FileSystemEntry[] = [];
  for (const it of items) {
    if (it.kind !== 'file' || typeof it.webkitGetAsEntry !== 'function') continue;
    const e = it.webkitGetAsEntry();
    if (e) entries.push(e);
  }
  if (entries.length) {
    for (const e of entries) await walkEntry(e, '', out);
    return out;
  }
  return filesFromInput(dt.files);
}

// ── 해시 ──
export async function sha256Hex(file: Blob): Promise<string> {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

// ── 배치 계획 ──
export interface BatchPlan<T extends { size: number }> {
  batches: T[][];
  oversize: T[];
}

/** 파일을 요청 단위로 묶는다: 파일 수·바이트 상한 둘 다 지킨다. 상한을 혼자 넘는 파일은 oversize 로 분리. */
export function planBatches<T extends { size: number }>(
  files: T[],
  caps: { maxFiles?: number; maxBytes?: number } = {},
): BatchPlan<T> {
  const maxFiles = caps.maxFiles ?? REQUEST_FILE_CAP;
  const maxBytes = caps.maxBytes ?? REQUEST_BYTE_CAP;
  const batches: T[][] = [];
  const oversize: T[] = [];
  let cur: T[] = [];
  let curBytes = 0;
  for (const f of files) {
    if (f.size > maxBytes) {
      oversize.push(f);
      continue;
    }
    if (cur.length >= maxFiles || curBytes + f.size > maxBytes) {
      if (cur.length) batches.push(cur);
      cur = [];
      curBytes = 0;
    }
    cur.push(f);
    curBytes += f.size;
  }
  if (cur.length) batches.push(cur);
  return { batches, oversize };
}

/** 고정 동시성으로 작업을 소진한다. 개별 실패는 worker 가 처리하고 여기서는 삼키지 않는다. */
export async function runPool<T>(items: T[], concurrency: number, worker: (item: T, index: number) => Promise<void>): Promise<void> {
  let next = 0;
  const lanes = Array.from({ length: Math.max(1, Math.min(concurrency, items.length)) }, async () => {
    for (;;) {
      const i = next++;
      if (i >= items.length) return;
      await worker(items[i]!, i);
    }
  });
  await Promise.all(lanes);
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  if (bytes < 1024) return bytes + ' B';
  const kb = bytes / 1024;
  if (kb < 1024) return (kb < 10 ? kb.toFixed(1) : Math.round(kb)) + ' KB';
  const mb = kb / 1024;
  if (mb < 1024) return (mb < 10 ? mb.toFixed(1) : Math.round(mb)) + ' MB';
  return (mb / 1024).toFixed(2) + ' GB';
}
